import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

const HIGHLIGHT_CLASS = 'skill-command-highlight';

/** Exported so tests can pull the plugin off the editor state. */
export const skillHighlightPluginKey = new PluginKey('skillHighlight');

export type SkillHighlightOptions = {
  /** Known skill slugs to highlight. A getter because skills load async — see SlashCommandOptions. */
  getSlugs: () => Set<string>;
};

/**
 * Tiptap extension that highlights `/skill-name` tokens in the editor when
 * the slug matches a known skill. Applies an inline decoration (blue text
 * on light blue background) to make skill invocations visually distinct.
 */
export const SkillHighlightExtension = Extension.create<SkillHighlightOptions>({
  name: 'skillHighlight',

  addOptions() {
    return {
      getSlugs: () => new Set<string>(),
    };
  },

  addProseMirrorPlugins() {
    const options = this.options;

    return [
      new Plugin({
        key: skillHighlightPluginKey,
        props: {
          decorations: (state) => {
            const slugs = options.getSlugs();
            if (slugs.size === 0) return DecorationSet.empty;

            const decorations: Decoration[] = [];

            state.doc.descendants((node, pos) => {
              if (!node.isText || node.text == null) return;

              // Match /slug patterns preceded by whitespace or at start of text.
              // The lookbehind prevents false positives inside URLs like
              // https://example.com/weekly-report.
              const regex = /(?<=^|\s)\/[\w-]+/g;
              let match: RegExpExecArray | null;

              // oxlint-disable-next-line no-cond-assign
              while ((match = regex.exec(node.text)) !== null) {
                const slug = match[0].slice(1); // remove leading /
                if (slugs.has(slug)) {
                  const from = pos + match.index;
                  const to = from + match[0].length;
                  decorations.push(
                    Decoration.inline(from, to, {
                      class: HIGHLIGHT_CLASS,
                    }),
                  );
                }
              }
            });

            return DecorationSet.create(state.doc, decorations);
          },
        },
      }),
    ];
  },
});
